import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { TextTools } from 'appshared/text-tools';
import { MiscTools } from 'appshared/misc-tools';
import { ValidationTools } from 'appshared/validation-tools';
import { UITools } from 'appshared/ui-tools';

import { AuthService } from 'client/app/services/auth.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';

import { PagingSearchBarComponent } from 'client/app/components/shared/paging-search-bar/paging-search-bar.component';

@Component({
	selector: 'app-table-zen-users',
	templateUrl: './table-zen-users.component.html',
	styleUrls: ['./table-zen-users.component.scss']
})
export class TableZenUsersComponent implements OnInit {
	ac = AppConstants;
	vt = ValidationTools;
	tt = TextTools;
	mt = MiscTools;
	ut = UITools;

	now = new Date();

	@ViewChild(PagingSearchBarComponent) pagingSearchBar: PagingSearchBarComponent = null;

	authUser: Models.AuthUser = null;
	loading: boolean = false;

	displayOptions: Models.TableDisplayOptions = new Models.TableDisplayOptions();
	pagingOptions: Models.TablePagingOptions = new Models.TablePagingOptions();
	storageKey: string = '';

	showSite: boolean = true;

	items: Models.ZenMasterUser[] = [];
	itemsToShow: Models.ZenMasterUser[] = [];

	columnDefs: Models.ColumnDefinition[] = [
		{
			field: 'is_enabled',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'User Account Enabled/Disabled',
			icon: 'power-off'
		}, {
			field: 'sso_id',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Single Sign On',
			icon: 'sign-in-alt'
		}, {
			field: 'is_admin',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Site Admin',
			icon: 'hat-wizard'
		}, {
			field: '__supportShort',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Zixi Support',
			icon: 'hat-wizard'
		}, {
			field: 'is_zixi_admin',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Zixi Admin',
			icon: 'hat-wizard'
		}, {
			field: 'name',
			type: 'baseText',
			sortType: 'text',
			label: 'Name',
			toolTip: null,
			icon: null
		}, {
			field: 'email',
			type: 'baseText',
			sortType: 'text',
			label: 'E-Mail',
			toolTip: null,
			icon: null
		}, {
			field: '__sinceAdd',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days since added',
			icon: 'clock'
		}, {
			field: '__sinceLogin',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days since last login',
			icon: 'sign-in-alt'
		}, {
			field: '__notificatioState',
			type: 'baseText',
			sortType: 'text',
			label: 'ZCP Notify',
			toolTip: null,
			icon: null
		}, {
			field: '__zenPrefix',
			type: 'baseText',
			sortType: 'text',
			label: 'Prefix',
			toolTip: null,
			icon: null
		}, {
			field: '__zenType',
			type: 'baseText',
			sortType: 'text',
			label: 'Site Type',
			toolTip: null,
			icon: null
		}, {
			field: '__roles',
			type: 'baseText',
			sortType: 'text',
			label: 'Role(s)',
			toolTip: null,
			icon: null
		}
	];

	fieldsToShow: string[] = [];
	allFieldsToShow: string[] = [];
	lockedFields: string[] = [];
	baselineFields: string[] = [];
	minimalFields: string[] = [];

	theForm: UntypedFormGroup;

	// things to choose from
	enabledChoices = [];
	zmTypeChoices = [];
	specialChoices = [
		{ value: 'enabled_user', label: 'User Account - Enabled' },
		{ value: 'basic_disabled', label: 'User Account - Disabled' },

		{ value: 'basic_user', label: 'Regular User' },
		{ value: 'admin_user', label: 'Site Admin' },
		{ value: 'support_readonly', label: 'Zixi Support (read-only)' },
		{ value: 'support_write', label: 'Zixi Support (read/write)' },
		{ value: 'zixi_admin', label: 'Zixi Admin' },

		{ value: 'some_logins', label: 'Has Logged In' },
		{ value: 'no_logins', label: 'Has Never Logged In' },

		{ value: 'sso_user', label: 'Uses Single Sign On' },
		{ value: 'non_sso_user', label: 'Does Not User Single Sign On' },
	];
	// filters
	enabledFilter: number = null;
	zmTypeFilters = [];
	specialFilters = [];

	// ------------------------------------------------------------------------
	constructor(
		private router: Router,
		private uiAlertsService: UiAlertsService,
		private authService: AuthService) {
	}

	// ------------------------------------------------------------------------
	ngOnInit(): void {
		const sessionUser: Models.AuthUser = this.authService.getUser();
		if (sessionUser != null && sessionUser.id !== 0) {
			this.authUser = sessionUser;
		} // if
	}

	// ------------------------------------------------------------------------
	updateContent(items: Models.ZenMasterUser[], storageKey: string, options: any = {}) {
		this.loading = true;

		this.items = items;
		this.storageKey = storageKey;

		let optionKeys: string[] = [];
		if (options) optionKeys = Object.keys(options);

		if (optionKeys.includes('showSite')) this.showSite = options.showSite;

		this.fieldsToShow = ['is_enabled', 'sso_id', 'is_admin', '__supportShort', 'is_zixi_admin', 'name', 'email', '__sinceAdd', '__sinceLogin', '__notificatioState'];

		if (this.showSite) {
			this.fieldsToShow.push('__zenPrefix');
			this.fieldsToShow.push('__zenType');
		} // if

		this.pagingSearchBar.setupPaging(this.items, this.columnDefs, this.storageKey, 'name', 'asc');

		let settings: any = {};
		try {
			if (localStorage.getItem(this.storageKey + '-filters'))
				settings = JSON.parse(localStorage.getItem(this.storageKey + '-filters'));
		} catch (e: any) { }
		const settingKeys: string[] = Object.keys(settings);

		if (this.showSite) {
			if (settingKeys.includes('enabledFilter')) this.enabledFilter = settings.enabledFilter;
			if (settingKeys.includes('zmTypeFilters')) this.zmTypeFilters = settings.zmTypeFilters;
		}

		let numWithRoles: number = 0;
		for (const item of items) {
			item['__roles'] = item.roles ? item.roles.sort().join('; ') : '';
			if (item.roles.length > 0) numWithRoles++;
		} // for

		if (numWithRoles > 0)
			this.fieldsToShow.push('__roles');

		if (settingKeys.includes('specialFilters')) this.specialFilters = settings.specialFilters;

		this.zmTypeChoices = [];
		this.zmTypeChoices.push({ value: '', label: 'Unknown/Not Set' });
		for (const zenMasterType of AppConstants.zenMasterTypes)
			this.zmTypeChoices.push({ value: zenMasterType, label: AppConstants.zenMasterTypeLabels[zenMasterType] });

		this.enabledChoices = [];
		this.enabledChoices.push({ value: 1, label: 'Enabled/Online (site)' });
		this.enabledChoices.push({ value: 0, label: 'Disabled/Offline (site)' });

		this.theForm = new UntypedFormGroup({
			enabledFilter: new UntypedFormControl(this.enabledFilter),
			zmTypeFilters: new UntypedFormControl(this.zmTypeFilters),
			specialFilters: new UntypedFormControl(this.specialFilters)
		});

		this.pagingSearchBar.setupPaging(this.items, this.columnDefs, this.storageKey, 'name', 'asc');

		this.loading = false;
	} // updateContent

	// ------------------------------------------------------------------------
	async onFormChange() {
		// console.log(this.theForm.value.objTypes);
		// console.log(this.theForm.value);

		this.enabledFilter = this.theForm.value.enabledFilter;
		this.zmTypeFilters = this.theForm.value.zmTypeFilters;
		this.specialFilters = this.theForm.value.specialFilters;

		let settings: any = {
			enabledFilter: this.enabledFilter,
			zmTypeFilters: this.zmTypeFilters,
			specialFilters: this.specialFilters
		};
		localStorage.setItem(this.storageKey + '-filters', JSON.stringify(settings));

		this.setSortBy(null);
	}

	// ------------------------------------------------------------------------
	filterItems(matchWords: string[]): any[] {

		const tmpList: Models.ZenMasterUser[] = [];
		for (const item of this.items) {
			let passedEnabledFilter = false;
			let passedTypeFilter = false;
			let passedSpecialFilters = false;

			if (this.enabledFilter == null || this.enabledFilter === item['__zenEnabled'])
				passedEnabledFilter = true;

			if (!this.zmTypeFilters || this.zmTypeFilters.length === 0
				|| (this.zmTypeFilters.length !== 0 && item['__zenType'] && this.zmTypeFilters.includes(item['__zenType'])))
				passedTypeFilter = true;

			passedSpecialFilters = true;
			if (!this.specialFilters || this.specialFilters.length === 0) {
				passedSpecialFilters = true;
			} else {
				passedSpecialFilters = true;

				if (this.specialFilters.includes('enabled_user') && (item.is_enabled === 0))
					passedSpecialFilters = false;

				if (this.specialFilters.includes('basic_disabled') && (item.is_enabled === 1))
					passedSpecialFilters = false;

				if (this.specialFilters.includes('basic_user') && (item.is_admin && item.is_admin === 1))
					passedSpecialFilters = false;

				if (this.specialFilters.includes('admin_user') && (!item.is_admin || item.is_admin === 0))
					passedSpecialFilters = false;

				if (this.specialFilters.includes('zixi_admin') && (!item.is_zixi_admin || item.is_zixi_admin === 0))
					passedSpecialFilters = false;

				if (this.specialFilters.includes('support_readonly') && ValidationTools.getZenUserSupportLabel(item, true) !== 'r')
					passedSpecialFilters = false;

				if (this.specialFilters.includes('support_write') && ValidationTools.getZenUserSupportLabel(item, true) !== 'w')
					passedSpecialFilters = false;

				if (this.specialFilters.includes('some_logins') && item.last_login_at == null)
					passedSpecialFilters = false;

				if (this.specialFilters.includes('no_logins') && item.last_login_at != null)
					passedSpecialFilters = false;

				if (this.specialFilters.includes('sso_user') && (!item.sso_id || item.sso_id === ''))
					passedSpecialFilters = false;

				if (this.specialFilters.includes('non_sso_user') && (item.sso_id && item.sso_id !== ''))
					passedSpecialFilters = false;
			} // if

			let match: boolean = passedEnabledFilter && passedTypeFilter && passedSpecialFilters;
			if (matchWords.length > 0) {
				let matches: number = 0;
				for (const w of matchWords) {
					if (item.name.toLowerCase().includes(w)
						|| (item.email && item.email.toLowerCase().includes(w))
						|| (item['__notificatioState'] && item['__notificatioState'].toLowerCase().includes(w))
						|| (item['__zenPrefix'] && item['__zenPrefix'].toLowerCase().includes(w))
						|| (item['__zenType'] && item['__zenType'].toLowerCase().includes(w))
						|| (item['__roles'] && item['__roles'].toLowerCase().includes(w)))
						matches++;
				} // for
				if (matches !== matchWords.length) match = false;
			} // if

			if (match) tmpList.push(item);
		} // for

		return tmpList;
	} // filterItems

	// ------------------------------------------------------------------------
	setSortBy(field: string) {
		this.pagingSearchBar.setSortBy(field);
	} // setSortBy

	// ------------------------------------------------------------------------
	// Catch calls from the paging/search bar
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			filterItems: (matchWords: string[]) => {
				return this.filterItems(matchWords);
			},
			updateItemsToShow: (itemsToShow: any[], displayOptions: Models.TableDisplayOptions, pagingOptions: Models.TablePagingOptions) => {
				this.itemsToShow = itemsToShow;
				this.displayOptions = displayOptions;
				this.pagingOptions = pagingOptions;
			},
			updateFieldsToShow: (fieldsToShow: string[]) => {
				this.fieldsToShow = fieldsToShow;
			},
			setPageNum: (pageNum: number, uniqueId: string) => {
				if (this.pagingSearchBar) this.pagingSearchBar.setPageNum(pageNum);
			},
			setSortBy: (field: string) => {
				this.setSortBy(field);
			}
		}
	}

}
